import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Star, Play, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';
import SEOHead, { localBusinessSchema, reviewSchema, breadcrumbSchema, faqSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};


// Testimonials slider component
const TestimonialsSlider = ({ testimonials, googleReviewsUrl }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, testimonials]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-testid^="testimonial-card"]')?.offsetWidth || 340;
    el.scrollBy({ left: dir * (cardWidth + 32), behavior: 'smooth' });
  };

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="testimonials-section" aria-label="Client testimonials and reviews">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">Testimonials</p>
          <h2 className="font-presti text-3xl md:text-4xl text-black/80">What Our Couples Say</h2>
        </div>

        {/* Slider container */}
        <div className="relative">
          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll(-1)}
              className="absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              data-testid="testimonials-prev-btn"
              aria-label="Previous testimonials"
            >
              <ChevronLeft size={20} className="text-black/70" />
            </button>
          )}

          {/* Right arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll(1)}
              className="absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              data-testid="testimonials-next-btn"
              aria-label="Next testimonials"
            >
              <ChevronRight size={20} className="text-black/70" />
            </button>
          )}

          {/* Scrollable track */}
          <div
            ref={scrollRef}
            className="flex gap-8 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {testimonials.map((t, i) => (
              <article
                key={t.id || i}
                className="group relative bg-[#fafafa] rounded-lg p-8 transition-all hover:shadow-lg flex-shrink-0 w-[320px] md:w-[360px]"
                style={{ scrollSnapAlign: 'start' }}
                data-testid={`testimonial-card-${i}`}
                itemScope
                itemType="https://schema.org/Review"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, si) => (
                    <Star key={si} size={14} className="fill-[#D4A574] text-[#D4A574]" />
                  ))}
                </div>

                {/* Review text */}
                <blockquote className="text-sm text-black/60 leading-relaxed mb-6 italic" itemProp="reviewBody">
                  "{t.text}"
                </blockquote>

                {/* Client info */}
                <div className="flex items-center gap-3">
                  {t.image_url ? (
                    <img src={t.image_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center">
                      <span className="text-lg font-presti text-black/40">{t.name?.[0]}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-black/80" itemProp="author">{t.name}</p>
                    {t.role && <p className="text-xs text-black/40">{t.role}</p>}
                  </div>
                  {t.video_url && (
                    <a
                      href={t.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto w-9 h-9 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10 transition-colors"
                      data-testid={`testimonial-video-${i}`}
                      aria-label={`Watch ${t.name}'s wedding film`}
                    >
                      <Play size={14} className="text-black/50 ml-0.5" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* View All Reviews Button */}
        <div className="text-center mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Magnetic>
            <Link
              to="/reviews"
              className="group inline-flex items-center gap-3 px-8 py-3.5 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/85 transition-all"
              data-testid="view-all-reviews-btn"
            >
              <span>View All Reviews</span>
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </Magnetic>
          {googleReviewsUrl && (
            <a
              href={googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-black/50 hover:text-black transition-colors"
              data-testid="google-reviews-link"
            >
              <span>Google Reviews</span>
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};


const AboutPage = () => {
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState({
    heading: 'About Crystal',
    subheading: 'Preserving the essence of your most cherished moments',
    about_image_url: '',
    description_paragraphs: [],
    why_choose_heading: 'Why Choose Crystal',
    why_choose_points: [],
    stats: [],
    gif_strip_enabled: true,
    experience_section_enabled: true,
    faq_section_enabled: true,
    cta_text: '',
    cta_button_text: 'Inquire',
  });
  const [logoUrl, setLogoUrl] = useState('');
  const [descriptionFont, setDescriptionFont] = useState('playfair');
  const [editorial, setEditorial] = useState({});
  const [aboutSettings, setAboutSettings] = useState({
    gif_strip_enabled: true,
    experience_section_enabled: true,
    faq_section_enabled: true
  });
  const [faqs, setFaqs] = useState([]);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [googleReviewsUrl, setGoogleReviewsUrl] = useState('');
  
  // Default stats as fallback
  const [stats, setStats] = useState([
    { number: '200+', label: 'Weddings Filmed' },
    { number: '8+', label: 'Years Experience' },
    { number: '50+', label: 'Awards Won' },
    { number: '8K', label: 'Cinema Cameras' }
  ]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, contentRes, editorialRes, aboutSettingsRes, faqsRes, testimonialsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/content/about-content`),
          fetch(`${API_URL}/api/content/editorial`),
          fetch(`${API_URL}/api/content/about-settings`).catch(() => ({ json: () => ({}) })),
          fetch(`${API_URL}/api/content/faqs`),
          fetch(`${API_URL}/api/content/testimonials`)
        ]);

        const settingsData = await settingsRes.json();
        const contentData = await contentRes.json();
        const editorialData = await editorialRes.json();
        const aboutSettingsData = await aboutSettingsRes.json();
        const faqsData = await faqsRes.json();
        const testimonialsData = await testimonialsRes.json();
        
        setSettings(settingsData);
        if (contentData) {
          setContent(contentData);
          if (contentData.stats && contentData.stats.length > 0) {
            setStats(contentData.stats);
          }
        }
        
        // Create editorial map
        const editorialMap = {};
        editorialData.forEach(section => {
          editorialMap[section.section_name] = section;
        });
        setEditorial(editorialMap);
        
        if (aboutSettingsData && Object.keys(aboutSettingsData).length > 0) {
          setAboutSettings(prev => ({ ...prev, ...aboutSettingsData }));
        }
        
        setFaqs(faqsData || []);
        setTestimonials(testimonialsData || []);
        setGoogleReviewsUrl(settingsData.google_reviews_url || '');
        
        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');
        
        if (settingsData.description_font) {
          setDescriptionFont(settingsData.description_font);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SEOHead
        title="About Us"
        description="Meet the Crystal Media team — luxury wedding photographers and cinematographers with 14+ years of experience. Specializing in Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural wedding photography and videography in Toronto, Vancouver, Montreal, and across Canada and the USA."
        url="/about"
        keywords={['About Crystal Media', 'Wedding Photography Team Toronto', 'South Asian Wedding Photographers', 'Luxury Wedding Team Canada']}
        siteSettings={settings || {}}
        jsonLd={[
          localBusinessSchema,
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
          ...(testimonials.length > 0 ? [reviewSchema(testimonials)] : []),
          ...(faqs.length > 0 ? [faqSchema(faqs)] : []),
        ]}
      />
      {/* Shared Header */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* Main Content */}
      <main className="pt-32 lg:pt-40">
        {/* Hero Section */}
        <section className="px-6 lg:px-12 max-w-6xl mx-auto mb-20 lg:mb-32">
          <Reveal><p className="text-xs tracking-[0.3em] uppercase text-black/40 mb-6">Our Story</p></Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-presti text-4xl md:text-5xl lg:text-6xl text-black mb-8">
              {content?.heading || 'About Crystal'}
            </h1>
          </Reveal>
          <Reveal delay={0.16}><div className="w-24 h-px bg-black/20" /></Reveal>
        </section>

        {/* Main Content Grid */}
        <section className="px-6 lg:px-12 max-w-6xl mx-auto mb-24 lg:mb-40">
          <Stagger className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24" stagger={0.15}>
            {/* About Image */}
            <StaggerItem y={40} className="relative">
              {content?.about_image_url ? (
                <div className="aspect-[4/5] relative overflow-hidden" data-testid="about-page-image">
                  <img src={content.about_image_url} alt="About Crystal Media" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="aspect-[4/5] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-black/20 text-sm tracking-widest uppercase">About Image</span>
                  </div>
                </div>
              )}
            </StaggerItem>

            {/* Text Content */}
            <StaggerItem y={40} className="flex flex-col justify-center">
              <h2 className="font-presti text-2xl md:text-3xl text-black mb-10 italic leading-relaxed">
                {content?.subheading || 'Preserving the essence of your most cherished moments'}
              </h2>
              
              <div className={`space-y-6 text-black/60 leading-relaxed font-desc-${descriptionFont}`}>
                {content?.description_paragraphs ? (
                  content.description_paragraphs.map((para, index) => (
                    <p key={index}>{para}</p>
                  ))
                ) : (
                  <>
                    <p>
                      Crystal is a collective of award-winning filmmakers and photographers 
                      based in {settings?.location || 'Toronto, Canada'}. For over a decade, we have been dedicated to 
                      transforming real love stories into timeless cinema.
                    </p>
                    <p>
                      Whether it is a traditional ceremony, a modern fusion wedding, or a blend 
                      of both, we capture every moment, every intricate detail, and every heartfelt 
                      emotion. Our approach is intentional and deeply personal.
                    </p>
                    <p>
                      We believe exceptional work requires presence and intention. For that reason, 
                      we accept a limited number of clients each year, ensuring every experience 
                      receives our full focus and creative energy.
                    </p>
                  </>
                )}
              </div>

              <Link
                to="/inquire"
                className="inline-flex items-center gap-3 mt-12 text-xs tracking-[0.2em] uppercase text-black border-b border-black pb-2 hover:opacity-60 transition-opacity duration-300 w-fit"
              >
                <span>Work With Us</span>
                <ArrowRight size={14} />
              </Link>
            </StaggerItem>
          </Stagger>
        </section>

        {/* Why Choose Us - with artistic background */}
        <section className="relative py-24 lg:py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-black/[0.02] via-black/[0.04] to-black/[0.02]" />
          <div className="absolute left-0 top-1/2 w-1/3 h-96 bg-gradient-to-r from-black/[0.03] to-transparent -translate-y-1/2" />
          
          <div className="px-6 lg:px-12 max-w-6xl mx-auto relative">
            <Reveal className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase text-black/40 mb-4">The Difference</p>
              <h2 className="font-presti text-3xl md:text-4xl text-black">
                {content.why_choose_heading || 'Why Choose Crystal'}
              </h2>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
              {(content.why_choose_points || []).map((item, index) => (
                <div key={index} className="group">
                  <div className="w-8 h-px bg-black/20 mb-6 group-hover:w-12 transition-all duration-500" />
                  <h3 className="text-sm tracking-wide font-medium text-black mb-3">{item.title}</h3>
                  <p className={`text-black/50 text-sm leading-relaxed font-desc-${descriptionFont}`}>{item.description}</p>
                </div>
              ))}
            </div>

            {/* Why Us CTA */}
            <div className="text-center mt-16">
              <Link to="/crystal-difference" className="inline-flex items-center gap-2 px-8 py-3 border border-black/20 text-xs tracking-[0.2em] uppercase text-black/70 hover:bg-black hover:text-white transition-all duration-300" data-testid="about-why-us-cta">
                Why Us — The Crystal Difference
              </Link>
            </div>
          </div>
        </section>

        {/* GIF Strip Section */}
        {(content.gif_strip_enabled !== false) && editorial.gif_strip?.is_active !== false && editorial.gif_strip?.gifs?.length > 0 && (
          <section className="py-16 lg:py-24 overflow-hidden">
            <div className="text-center mb-10">
              <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">
                {editorial.gif_strip?.subtitle || 'Featured Moments'}
              </p>
              <h2 className="font-presti text-2xl md:text-3xl text-black/80">
                {editorial.gif_strip?.title || 'Captured With Love'}
              </h2>
            </div>
            
            {/* Horizontal scrolling GIF strip */}
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-6 px-6 lg:px-12" style={{ minWidth: 'max-content' }}>
                {editorial.gif_strip.gifs.map((gif, index) => (
                  <div 
                    key={gif.id || index} 
                    className="flex-shrink-0 overflow-hidden rounded-lg shadow-lg"
                    style={{ aspectRatio: gif.aspect_ratio || '4/5' }}
                  >
                    <img
                      src={gif.url}
                      alt={gif.label || 'Featured moment'}
                      className="h-64 w-auto object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Experience Section */}
        {(content.experience_section_enabled !== false) && editorial.experience?.is_active !== false && (
          <section className="py-24 lg:py-32 relative bg-gradient-to-b from-[#f5f5f5] to-[#fafafa]">
            <div className="max-w-6xl mx-auto px-6 lg:px-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Text Content */}
                <div>
                  <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-6">
                    {editorial.experience?.subtitle || 'The Experience'}
                  </p>
                  <h2 className="font-presti text-3xl md:text-4xl text-black/90 mb-8 leading-tight">
                    {editorial.experience?.title || 'More than just a video'}
                  </h2>
                  <p className={`text-black/50 text-lg leading-relaxed italic font-desc-${descriptionFont}`}>
                    {editorial.experience?.description || 'We believe in creating art that moves you. Every frame is carefully crafted to evoke emotion and preserve the essence of your celebration.'}
                  </p>
                </div>
                
                {/* GIFs */}
                {editorial.experience?.gifs?.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {editorial.experience.gifs.slice(0, 2).map((gif, index) => (
                      <div 
                        key={gif.id || index}
                        className="overflow-hidden rounded-lg shadow-xl"
                        style={{ aspectRatio: gif.aspect_ratio || '4/5' }}
                      >
                        <img
                          src={gif.url}
                          alt={gif.label || 'Experience'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Stats */}
        <section className="py-24 lg:py-32">
          <div className="max-w-4xl mx-auto px-6">
            <Stagger className={`grid gap-8 lg:gap-16 ${stats.length === 4 ? 'grid-cols-2 md:grid-cols-4 gap-y-12' : 'grid-cols-3'}`} stagger={0.12} data-testid="about-stats">
              {stats.map((stat) => (
                <StaggerItem key={stat.label} className="text-center" data-testid={`about-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <p className="font-presti text-4xl lg:text-5xl text-black mb-2">{stat.number}</p>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-black/40">{stat.label}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* ─── Testimonials Section ─── */}
        {testimonials.length > 0 && (
          <TestimonialsSlider testimonials={testimonials} googleReviewsUrl={googleReviewsUrl} />
        )}

        {/* FAQ Section */}
        {(content.faq_section_enabled !== false) && faqs.length > 0 && (
          <section className="py-20 lg:py-28 bg-white" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-6">
              <div className="text-center mb-12">
                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">Questions</p>
                <h2 className="font-presti text-3xl md:text-4xl text-black/80">Frequently Asked</h2>
              </div>
              
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={faq.id || index}
                    className="border border-black/10 rounded-lg overflow-hidden"
                    data-testid={`faq-item-${index}`}
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-black/[0.02] transition-colors"
                    >
                      <span className="font-medium text-black/80 pr-4">{faq.question}</span>
                      <ChevronDown 
                        size={20} 
                        className={`text-black/40 flex-shrink-0 transition-transform duration-300 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <p className={`px-6 pb-5 text-black/50 leading-relaxed font-desc-${descriptionFont}`}>
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-20 lg:py-28 border-t border-black/5">
          <Reveal className="max-w-2xl mx-auto px-6 text-center">
            <p className="font-presti text-2xl md:text-3xl text-black mb-10 italic">
              Ready to create something beautiful?
            </p>
            <Magnetic>
              <Link
                to="/inquire"
                data-testid="about-cta-inquire-btn"
                className="inline-flex items-center gap-3 px-10 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/80 transition-colors duration-300"
              >
                <span>Inquire</span>
                <ArrowRight size={14} />
              </Link>
            </Magnetic>
          </Reveal>
        </section>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-black/5">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            {logoUrl ? (
              <img src={logoUrl} alt="Crystal Media" className="h-6 w-auto opacity-50" />
            ) : (
              <span className="font-presti text-xs tracking-[0.2em] text-black/40">CRYSTAL MEDIA</span>
            )}
            <p className="text-[10px] tracking-[0.15em] uppercase text-black/30">
              © 2025 Crystal Media. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default AboutPage;
